import { useState, useMemo } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useCurrentPlan } from '@/hooks/useCurrentPlan';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { LanguageToggle } from '@/components/ui/LanguageSelector';
import { Badge } from '@/components/ui/badge';
import { UpsellModal } from '@/components/upsell/UpsellModal';
import {
  LayoutDashboard, Users, FileText, Globe, Settings,
  LogOut, Menu, X, ChevronRight, ChevronDown, Building2, Shield, Bot,
  UserCheck, HardHat, Wrench, Clock, Sparkles, Lightbulb,
  Link2, CalendarDays, Lock, Gem
} from 'lucide-react';

export function DashboardLayout() {
  const location = useLocation();
  const { user, workspace, logout } = useAuth();
  const { t, language } = useLanguage();
  const { planId, plan, isLocked } = useCurrentPlan();
  const tf = (fr, en) => (language === 'fr' ? fr : en);

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [marketingOpen, setMarketingOpen] = useState(
    location.pathname.startsWith('/seo')
  );
  const [upsellFeature, setUpsellFeature] = useState(null);

  const perms = user?.permissions || {};
  const isPlatformAdmin = user?.role === 'admin';

  // ---------------------------------------------------------------------------
  // Marketing IA sub-menu (replaces the old single "Agent SEO" item)
  // ---------------------------------------------------------------------------
  const marketingChildren = [
    { tab: 'competitors',   label: tf('Concurrents', 'Competitors'),    icon: Users },
    { tab: 'opportunities', label: tf('Opportunites', 'Opportunities'), icon: Lightbulb },
    { tab: 'articles',      label: tf('Articles', 'Articles'),          icon: FileText },
    { tab: 'backlinks',     label: 'Backlinks',                          icon: Link2 },
    { tab: 'generator',     label: tf('Generateur', 'Generator'),       icon: Sparkles },
    { tab: 'calendar',      label: tf('Calendrier', 'Calendar'),        icon: CalendarDays },
  ];

  // ---------------------------------------------------------------------------
  // Flat nav items (built dynamically so feature-gated items can be locked
  // visually rather than removed from the UI).
  // ---------------------------------------------------------------------------
  const NAV_ITEMS = useMemo(() => {
    const items = [
      { path: '/dashboard', label: t('nav.dashboard'), icon: LayoutDashboard, perm: 'can_view_dashboard' },
      { path: '/leads', label: t('nav.leads'), icon: Users, perm: 'can_view_leads' },
      { path: '/leads/pipeline', label: t('nav.pipeline'), icon: ChevronRight, indent: true, perm: 'can_view_leads' },
      { path: '/clients', label: 'Clients', icon: UserCheck, perm: 'can_view_leads' },
      { path: '/employees', label: tf('Employes', 'Employees'), icon: HardHat, perm: 'can_manage_employees', feature: 'multi_user' },
      { path: '/subcontractors', label: tf('Sous-traitants', 'Subcontractors'), icon: Wrench, perm: 'can_manage_subcontractors', feature: 'multi_user' },
      { path: '/forms', label: t('nav.forms'), icon: FileText, perm: 'can_manage_forms' },
      { path: '/landing-pages', label: t('nav.landingPages'), icon: Globe, perm: 'can_manage_pages' },
      // Marketing IA group is rendered separately (see below)
      { path: '/timetracking', label: tf('Feuille de temps', 'Timesheet'), icon: Clock, perm: 'can_manage_employees', feature: 'multi_user' },
      { path: '/settings', label: t('nav.settings'), icon: Settings, perm: 'can_manage_settings' },
    ];
    return items.filter((it) => perms[it.perm]);
  }, [perms, t, language]); // eslint-disable-line react-hooks/exhaustive-deps

  // Marketing IA visibility — Option 1 gating: the menu is visible whenever
  // the plan unlocks `seo_articles` (Pro / Enterprise) OR the role grants
  // `can_manage_seo` OR the caller is a workspace owner (admin / admin_enterprise)
  // — the latter so a Starter owner still sees the menu in its LOCKED state
  // and can click through to the upsell modal. The "locked vs unlocked"
  // distinction below is purely visual; backend gating is unchanged.
  const role = (user?.role || '').toLowerCase();
  const isWorkspaceAdmin = role === 'admin' || role === 'admin_enterprise';
  const showMarketing =
    !!perms.can_manage_seo ||
    !isLocked('seo_articles') ||
    isWorkspaceAdmin;
  const marketingLocked = isLocked('seo_articles');

  // Click handler used by locked items
  const handleLockedClick = (feature, e) => {
    e.preventDefault();
    e.stopPropagation();
    setUpsellFeature(feature);
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  const isActive = (path) => {
    if (path === '/leads/pipeline') return location.pathname === path;
    if (path === '/leads') {
      return location.pathname.startsWith('/leads') && !location.pathname.includes('pipeline');
    }
    return location.pathname.startsWith(path);
  };

  const isMarketingActive = location.pathname.startsWith('/seo');

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------
  const renderNavLink = (item) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    const locked = item.feature ? isLocked(item.feature) : false;

    const baseCls = `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
      item.indent ? 'ml-6' : ''
    } ${
      active
        ? 'bg-primary text-primary-foreground font-semibold'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    } ${locked ? 'opacity-60' : ''}`;

    if (locked) {
      return (
        <button
          key={item.path}
          type="button"
          onClick={(e) => handleLockedClick(item.feature, e)}
          className={`${baseCls} w-full text-left cursor-pointer`}
          data-testid={`nav-locked-${item.path.replace(/\//g, '-')}`}
        >
          <Icon className="h-4 w-4" />
          <span className="flex-1">{item.label}</span>
          <Lock className="h-3.5 w-3.5 text-amber-500" data-testid={`lock-${item.feature}`} />
        </button>
      );
    }

    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={baseCls}
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
      >
        <Icon className="h-4 w-4" />
        <span>{item.label}</span>
      </Link>
    );
  };

  const renderMarketingGroup = () => {
    if (!showMarketing) return null;

    const groupBaseCls = `flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors w-full text-left ${
      isMarketingActive && !marketingLocked
        ? 'bg-primary text-primary-foreground font-semibold'
        : 'text-muted-foreground hover:bg-muted hover:text-foreground'
    } ${marketingLocked ? 'opacity-60' : ''}`;

    if (marketingLocked) {
      return (
        <button
          type="button"
          onClick={(e) => handleLockedClick('seo_articles', e)}
          className={groupBaseCls}
          data-testid="nav-locked-marketing-ia"
        >
          <Bot className="h-4 w-4" />
          <span className="flex-1">{tf('Marketing IA', 'AI Marketing')}</span>
          <Lock className="h-3.5 w-3.5 text-amber-500" data-testid="lock-seo_articles" />
        </button>
      );
    }

    return (
      <div className="space-y-1">
        <button
          type="button"
          onClick={() => setMarketingOpen((v) => !v)}
          className={groupBaseCls}
          data-testid="nav-marketing-ia-toggle"
          aria-expanded={marketingOpen}
        >
          <Bot className="h-4 w-4" />
          <span className="flex-1">{tf('Marketing IA', 'AI Marketing')}</span>
          {marketingOpen ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {marketingOpen && (
          <div className="ml-3 border-l border-border pl-3 space-y-1">
            {marketingChildren.map((child) => {
              const ChildIcon = child.icon;
              const childPath = `/seo?tab=${child.tab}`;
              const activeChild =
                isMarketingActive &&
                (location.search.includes(`tab=${child.tab}`) ||
                  (!location.search && child.tab === 'competitors'));
              return (
                <Link
                  key={child.tab}
                  to={childPath}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors ${
                    activeChild
                      ? 'bg-primary/10 text-primary font-semibold'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid={`nav-marketing-${child.tab}`}
                >
                  <ChildIcon className="h-3.5 w-3.5" />
                  <span>{child.label}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Plan badge color
  const planBadgeCls =
    planId === 'enterprise'
      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0'
      : planId === 'pro'
      ? 'bg-gradient-to-r from-amber-400 to-orange-500 text-white border-0'
      : 'bg-muted text-muted-foreground';

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-4 border-b border-border">
            <Link to="/dashboard" className="flex items-center gap-2">
              <span className="font-heading text-xl font-black tracking-tight">
                Space<span className="text-primary">LeadX</span>
              </span>
            </Link>
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" />
            </Button>
          </div>

          {/* Workspace + Plan badge */}
          {(workspace || isPlatformAdmin) && (
            <div className="p-4 border-b border-border">
              <div className="flex items-center gap-3 p-2 rounded-lg bg-muted/50">
                <div className="w-8 h-8 rounded bg-primary/10 flex items-center justify-center">
                  <Building2 className="h-4 w-4 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold truncate">
                    {workspace?.name || tf('Plateforme', 'Platform')}
                  </p>
                  <Badge
                    className={`text-[10px] px-1.5 py-0 h-4 mt-0.5 ${planBadgeCls}`}
                    data-testid="sidebar-plan-badge"
                  >
                    {planId === 'enterprise' && <Gem className="h-2.5 w-2.5 mr-1" />}
                    {plan.name}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          {/* Navigation */}
          <ScrollArea className="flex-1 py-4">
            <nav className="px-3 space-y-1">
              {NAV_ITEMS.map((item) => renderNavLink(item))}
              {renderMarketingGroup()}

              {isPlatformAdmin && (
                <Link
                  to="/admin"
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                    isActive('/admin')
                      ? 'bg-primary text-primary-foreground font-semibold'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                  data-testid="nav--admin"
                >
                  <Shield className="h-4 w-4" />
                  <span>Admin</span>
                </Link>
              )}
            </nav>
          </ScrollArea>

          {/* User Menu (Bottom) */}
          <div className="p-4 border-t border-border">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="w-full justify-start gap-3 h-auto py-2">
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-primary/10 text-primary">
                      {user?.name?.charAt(0).toUpperCase() || '?'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 text-left min-w-0">
                    <p className="text-sm font-semibold truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>{t('nav.myAccount')}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <Settings className="mr-2 h-4 w-4" />
                    {t('nav.settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('auth.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-30 h-16 bg-background/95 backdrop-blur border-b border-border">
          <div className="flex items-center justify-between h-full px-4 md:px-6">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            </div>

            <div className="flex items-center gap-4">
              <LanguageToggle />
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 md:p-6 lg:p-8 max-w-7xl">
          <Outlet />
        </main>
      </div>

      {/* Upsell modal triggered by locked nav items */}
      <UpsellModal
        open={!!upsellFeature}
        onOpenChange={(o) => !o && setUpsellFeature(null)}
        feature={upsellFeature}
      />
    </div>
  );
}
